"use client";

import * as amplitude from "@amplitude/unified";

/**
 * True when analytics should be OFF: local development (`next dev`, NODE_ENV !== 'production')
 * or the page is being served from a localhost / *.local host. Keeps dev-server activity out
 * of real Amplitude product data. Deployed environments (preview, production) still track.
 */
export function isAmplitudeDisabled(): boolean {
  if (process.env.NODE_ENV !== "production") return true;
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "::1" ||
    h === "[::1]" ||
    h.endsWith(".local")
  );
}

export const trackEvent = (eventName: string, eventProperties?: Record<string, any>) => {
  if (typeof window === "undefined") return;
  if (isAmplitudeDisabled()) return;

  try {
    amplitude.track(eventName, eventProperties);
  } catch (error) {
    console.error("Amplitude trackEvent failed:", error);
  }
};

/**
 * Set Amplitude user properties (via Identify). Use for durable per-user attributes that
 * should segment every chart — character_count, theme, wrote_own_story, product_selected,
 * etc. — not for one-off event data. Null/undefined values are skipped.
 */
export const identifyUser = (props: Record<string, any>) => {
  if (typeof window === "undefined") return;
  if (isAmplitudeDisabled()) return;

  try {
    const identify = new amplitude.Identify();
    let any = false;
    for (const [key, value] of Object.entries(props)) {
      if (value === undefined || value === null) continue;
      identify.set(key, value);
      any = true;
    }
    if (any) amplitude.identify(identify);
  } catch (error) {
    console.error("Amplitude identifyUser failed:", error);
  }
};

/** Regeneration kinds, for the unified "Regeneration Requested" event. */
export type RegenerationType =
  | "cast"
  | "cover"
  | "back-cover"
  | "page-retry"
  | "page-magic-fix"
  | "page-regenerate"
  | "page-comments"
  | "full-comic"
  | "full-retry";

/**
 * One event for every paid re-generation the user asks for, on top of whatever specific
 * event the call site already fires. Lets you see total regen volume (and cost) per run and
 * per converted book without stitching six event names together.
 */
export const trackRegeneration = (
  type: RegenerationType,
  extra?: Record<string, any>,
) => {
  trackEvent("Regeneration Requested", { type, ...extra });
};
